import {
  I2cInput,
  I2cListItem,
  I2cRadio,
  I2cRadioGroup,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { isPushApiNotificationOptions, sendPushNotificationOptions } from "./stringUtils";
import { validationSchemaPushAPIs } from "./ValidationSchema";

const PushAPIs = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaPushAPIs),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      web_services_push_api_would_you_like_i2c_push_notification_to_send_notification_to_your_preferred_channels: "",
      web_services_push_api_please_confirm_channel_to_send_push_notification: "",
      web_services_push_api_please_confirm_channel_to_send_push_notification_other: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "web_services_push_api_would_you_like_i2c_push_notification_to_send_notification_to_your_preferred_channels",
    "web_services_push_api_please_confirm_channel_to_send_push_notification",
    "web_services_push_api_please_confirm_channel_to_send_push_notification_other",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData((prev) => ({
      ...prev,
      6: {
        ...prev[6],
        "6_1": { ...values },
      },
    }));
  }, [methods, setAddFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[6]?.["6_1"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("6_1", true);
    } else {
      setFormValidStates("6_1", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleRadioChange = (fieldName, value, fieldsToReset = {}) => {
    methods.setValue(fieldName, value, { shouldValidate: true });

    // Clear dependent fields if needed
    Object.keys(fieldsToReset).forEach(key => {
      methods.setValue(key, fieldsToReset[key]);
    });

    handleValueChanges();
  };

  const handleInputChange = (fieldName, value) => {
    methods.setValue(fieldName, value, { shouldValidate: true });
    handleValueChanges();
  };

  // Handler for Push API notification radio
  const handlePushApiChange = (e) => {
    const value = e.target?.value || e;
    handleRadioChange(
      "web_services_push_api_would_you_like_i2c_push_notification_to_send_notification_to_your_preferred_channels",
      value,
      value === "no" ? {
        web_services_push_api_please_confirm_channel_to_send_push_notification: "",
        web_services_push_api_please_confirm_channel_to_send_push_notification_other: "",
      } : {}
    );
  };

  // Handler for channel selection radio
  const handleChannelChange = (e) => {
    const value = e.target?.value || e;
    handleRadioChange(
      "web_services_push_api_please_confirm_channel_to_send_push_notification",
      value,
      value !== "other" ? {
        web_services_push_api_please_confirm_channel_to_send_push_notification_other: "",
      } : {}
    );
  };

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div className="sub-section" i2c-grid="12">
          <I2cRadioGroup
            label="Would you like to use i2c Push Notifications (API) to send notifications to your preferred channels?"
            value={methods.watch("web_services_push_api_would_you_like_i2c_push_notification_to_send_notification_to_your_preferred_channels")}
            onI2cChange={handlePushApiChange}
          >
            <div i2c-grid="grid gap-sm">
              {isPushApiNotificationOptions.map((radio, index) => (
                <I2cListItem
                  key={index}
                  i2c-grid="4@md"
                  variant="receipt"
                  label={radio.label}
                  type="checkbox"
                >
                  <I2cRadio slot="prefix" filled value={radio.value} />
                </I2cListItem>
              ))}
            </div>
          </I2cRadioGroup>
          {errors.web_services_push_api_would_you_like_i2c_push_notification_to_send_notification_to_your_preferred_channels && (
            <ErrorMessage show={true} message={errors.web_services_push_api_would_you_like_i2c_push_notification_to_send_notification_to_your_preferred_channels.message} />
          )}
        </div>
        <div className="sub-section" i2c-grid="12">
          {methods.watch("web_services_push_api_would_you_like_i2c_push_notification_to_send_notification_to_your_preferred_channels") === "yes" && (
            <>
              <div className="sub-section" i2c-grid="12">
                <I2cRadioGroup
                  label="Please confirm channel to send push notifications"
                  value={methods.watch("web_services_push_api_please_confirm_channel_to_send_push_notification")}
                  onI2cChange={handleChannelChange}
                >
                  <div i2c-grid="grid gap-sm">
                    {sendPushNotificationOptions.map((radio, index) => (
                      <I2cListItem
                        key={index}
                        i2c-grid="4@md"
                        variant="receipt"
                        label={radio.label}
                        type="checkbox"
                      >
                        <I2cRadio slot="prefix" filled value={radio.value} />
                      </I2cListItem>
                    ))}
                  </div>
                </I2cRadioGroup>
                {errors.web_services_push_api_please_confirm_channel_to_send_push_notification && (
                  <ErrorMessage show={true} message={errors.web_services_push_api_please_confirm_channel_to_send_push_notification.message} />
                )}
              </div>
            </>
          )}
          {methods.watch(
            "web_services_push_api_please_confirm_channel_to_send_push_notification",
          ) === "other" && (
            <div i2c-grid="grid gap-sm" className="mt-16">
              <div i2c-grid="4@md">
                <div className="single-field-wrapper">
                  <I2cInput
                    className="single-input-field"
                    placeholder="Please Specify"
                    size="large"
                    label="Pleae Specify"
                    name="web_services_push_api_please_confirm_channel_to_send_push_notification_other"
                    value={
                      methods.watch(
                        "web_services_push_api_please_confirm_channel_to_send_push_notification_other",
                      ) || ""
                    }
                    onI2cInput={(e) =>
                      handleInputChange(
                        "web_services_push_api_please_confirm_channel_to_send_push_notification_other",
                        e.target?.value || e.detail?.value || "",
                      )
                    }
                  />
                  {errors.web_services_push_api_please_confirm_channel_to_send_push_notification_other && (
                    <ErrorMessage
                      show={true}
                      message={
                        errors
                          .web_services_push_api_please_confirm_channel_to_send_push_notification_other
                          .message
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default PushAPIs;
